import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Paragraph from '../Paragraph/Paragraph';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';

// Label/value row for TargetPriceOrderReview (subtotal, savings, total at
// execution). GTT-owned so its styling can diverge from the shared
// InformationList/InformationListItem components used elsewhere in the app.
const ReviewInfoRowContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${pxToRem(16)};

  > :last-child {
    text-align: right;
  }
`;

// Paragraph has no breakpoint awareness of its own, so the mobile overrides
// are added here. Bold (isLabelBold/isValueBold) still wins on mobile, same
// as Paragraph's own desktop precedence, so the *Mobile weight never
// un-bolds a row that's deliberately bold (e.g. totalAtExecution).
const ReviewInfoLabel = styled(Paragraph)`
  ${mediaBelow(variables.breakpoints.bpMd)} {
    ${({ labelFontSizeMobile }) =>
      labelFontSizeMobile && `font-size: ${labelFontSizeMobile}px;`}
    font-weight: ${({ isLabelBold, labelFontWeightMobile }) =>
      isLabelBold ? 600 : labelFontWeightMobile};
  }
`;

const ReviewInfoValue = styled(Paragraph)`
  ${mediaBelow(variables.breakpoints.bpMd)} {
    ${({ valueFontSizeMobile }) =>
      valueFontSizeMobile && `font-size: ${valueFontSizeMobile}px;`}
    font-weight: ${({ isValueBold, valueFontWeightMobile }) =>
      isValueBold ? 600 : valueFontWeightMobile};
  }
`;

const ReviewInfoRow = ({
  label,
  value,
  fontFamily,

  labelFontSize,
  usePxForLabelFontSize,
  labelFontWeight,
  labelLineHeight,
  isLabelBold,
  labelColor,
  labelFontSizeMobile,
  labelFontWeightMobile,

  valueFontSize,
  usePxForValueFontSize,
  valueFontWeight,
  valueLineHeight,
  isValueBold,
  valueColor,
  valueFontSizeMobile,
  valueFontWeightMobile,
}) => (
  <ReviewInfoRowContainer>
    <ReviewInfoLabel
      fontFamily={fontFamily}
      fontSize={labelFontSize}
      usePx={usePxForLabelFontSize}
      fontWeight={labelFontWeight}
      lineHeight={labelLineHeight}
      bold={isLabelBold}
      color={labelColor}
      isLabelBold={isLabelBold}
      labelFontSizeMobile={labelFontSizeMobile}
      labelFontWeightMobile={labelFontWeightMobile}
    >
      {label}
    </ReviewInfoLabel>
    <ReviewInfoValue
      fontSize={valueFontSize}
      usePx={usePxForValueFontSize}
      fontWeight={valueFontWeight}
      lineHeight={valueLineHeight}
      bold={isValueBold}
      color={valueColor}
      isValueBold={isValueBold}
      valueFontSizeMobile={valueFontSizeMobile}
      valueFontWeightMobile={valueFontWeightMobile}
    >
      {value}
    </ReviewInfoValue>
  </ReviewInfoRowContainer>
);

ReviewInfoRow.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  fontFamily: PropTypes.string,

  labelFontSize: PropTypes.number,
  usePxForLabelFontSize: PropTypes.bool,
  labelFontWeight: PropTypes.number,
  labelLineHeight: PropTypes.string,
  isLabelBold: PropTypes.bool,
  labelColor: PropTypes.string,
  labelFontSizeMobile: PropTypes.number,
  labelFontWeightMobile: PropTypes.number,

  valueFontSize: PropTypes.number,
  usePxForValueFontSize: PropTypes.bool,
  valueFontWeight: PropTypes.number,
  valueLineHeight: PropTypes.string,
  isValueBold: PropTypes.bool,
  valueColor: PropTypes.string,
  valueFontSizeMobile: PropTypes.number,
  valueFontWeightMobile: PropTypes.number,
};

export default ReviewInfoRow;
